import { useCallback, useEffect, useState } from "react";
import { Boxes, History, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, brl, dataBR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { EmptyState, ErrorState, Field, Loading, Surface } from "@/components/ds";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";
import { NativeSelect } from "@/components/ds/NativeSelect";

const CATEGORY_LABEL = {
  ARGOLA: "Argolas",
  EMBALAGEM: "Embalagens",
  CAIXA: "Caixas",
  ETIQUETA: "Etiquetas",
  CORRENTE: "Correntes",
  ACESSORIO: "Acessórios",
  OUTRO: "Outros",
};

const EMPTY = {
  name: "",
  category: "ARGOLA",
  unit: "UN",
  min_stock: "",
  supplier: "",
  notes: "",
  initial_quantity: "",
  paid_value: "",
};

export function MaterialsPanel() {
  const [materials, setMaterials] = useState(null);
  const [meta, setMeta] = useState({ categories: [], units: [] });
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [moveFor, setMoveFor] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    setError("");
    try {
      const [m, t] = await Promise.all([api.get("/materials"), api.get("/materials/meta")]);
      setMaterials(m.data);
      setMeta(t.data);
    } catch (e) {
      setError(apiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const confirmDelete = async () => {
    try {
      await api.delete(`/materials/${toDelete.id}`);
      toast.success("Material excluído.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setToDelete(null);
    }
  };

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!materials) return <Loading label="Carregando materiais..." />;

  return (
    <div className="space-y-6" data-testid="materials-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-2xl text-sm text-muted-foreground">
          Argolas, embalagens, caixas, etiquetas e acessórios. A compra sai do caixa; o consumo na
          produção entra como custo, sem nova saída de dinheiro.
        </p>
        <Button onClick={() => setEditing("new")} data-testid="new-material-btn" className="h-11 gap-2 rounded-full px-5 font-semibold">
          <Plus className="h-4 w-4" /> Novo material
        </Button>
      </div>

      {materials.length === 0 ? (
        <EmptyState
          icon={Boxes}
          title="Nenhum material cadastrado"
          description="Cadastre argolas, embalagens e acessórios para usá-los nos produtos e no custo real."
          action={
            <Button onClick={() => setEditing("new")} data-testid="empty-new-material-btn" className="gap-2 rounded-full">
              <Plus className="h-4 w-4" /> Novo material
            </Button>
          }
        />
      ) : (
        <div className="grid gap-4 stagger md:grid-cols-2 xl:grid-cols-3">
          {materials.map((m) => (
            <Surface key={m.id} className="p-5" data-testid={`material-card-${m.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate font-display text-base font-medium" data-testid={`material-name-${m.id}`}>
                    {m.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {CATEGORY_LABEL[m.category] || m.category}
                    {m.supplier ? ` · ${m.supplier}` : ""}
                  </p>
                </div>
                {m.low_stock && (
                  <span
                    className="shrink-0 rounded-full border border-amber-500/40 bg-amber-500/15 px-2 py-0.5 text-[10px] uppercase tracking-wide text-amber-300"
                    data-testid={`material-low-${m.id}`}
                  >
                    Estoque baixo
                  </span>
                )}
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3 rounded-lg border border-white/10 bg-secondary/40 p-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Disponível</p>
                  <p className="font-num text-base" data-testid={`material-qty-${m.id}`}>
                    {Number(m.quantity || 0).toLocaleString("pt-BR")} {m.unit}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Custo unitário</p>
                  <p className="font-num text-base" data-testid={`material-cost-${m.id}`}>
                    {m.unit_cost ? brl(m.unit_cost) : "—"}
                  </p>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap justify-end gap-1 border-t border-white/10 pt-3">
                <Button variant="outline" size="sm" onClick={() => setMoveFor(m)} data-testid={`material-move-btn-${m.id}`} className="h-8 rounded-full px-3 text-xs">
                  Movimentar
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setHistoryFor(m)} data-testid={`material-history-btn-${m.id}`} className="gap-1.5 text-xs text-muted-foreground hover:text-foreground">
                  <History className="h-3.5 w-3.5" /> Histórico
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setEditing(m)} data-testid={`material-edit-btn-${m.id}`} className="gap-1.5 text-xs text-muted-foreground hover:text-foreground">
                  <Pencil className="h-3.5 w-3.5" /> Editar
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setToDelete(m)} data-testid={`material-delete-btn-${m.id}`} className="text-muted-foreground hover:text-destructive">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </Surface>
          ))}
        </div>
      )}

      {editing && (
        <MaterialModal
          material={editing === "new" ? null : editing}
          meta={meta}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}
      {moveFor && (
        <MaterialMovementModal material={moveFor} onClose={() => setMoveFor(null)} onSaved={load} />
      )}
      {historyFor && (
        <MaterialHistoryModal material={historyFor} onClose={() => setHistoryFor(null)} />
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="border-white/10 bg-card" data-testid="delete-material-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir material?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete?.name} será removido junto com seu histórico e as compras lançadas no
              Financeiro.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-material-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-material-btn" className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function MaterialModal({ material, meta, onClose, onSaved }) {
  const [form, setForm] = useState(
    material
      ? { ...EMPTY, ...material, min_stock: String(material.min_stock ?? "") }
      : EMPTY
  );
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = {
      name: form.name,
      category: form.category,
      unit: form.unit,
      min_stock: Number(form.min_stock || 0),
      supplier: form.supplier || "",
      notes: form.notes || "",
      active: true,
    };
    try {
      if (material) await api.put(`/materials/${material.id}`, body);
      else
        await api.post("/materials", {
          ...body,
          initial_quantity: Number(form.initial_quantity || 0),
          paid_value: form.paid_value === "" ? null : Number(form.paid_value),
        });
      toast.success(material ? "Material atualizado." : "Material cadastrado.");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title={material ? "Editar material" : "Novo material"}
      description="A quantidade é controlada na unidade que você escolher (UN, PAR, M...)."
      testid="material-modal"
    >
      <form onSubmit={submit} className="mt-2 flex flex-col gap-4">
        <Field label="Nome">
          <Input value={form.name} onChange={set("name")} required placeholder="Ex.: Argola niquelada 25mm" data-testid="material-name-input" />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Categoria">
            <NativeSelect
              value={form.category}
              onChange={(v) => setForm((f) => ({ ...f, category: v }))}
              options={(meta.categories || []).map((c) => ({ value: c, label: CATEGORY_LABEL[c] || c }))}
              data-testid="material-category-select"
            />
          </Field>
          <Field label="Unidade de controle">
            <NativeSelect
              value={form.unit}
              onChange={(v) => setForm((f) => ({ ...f, unit: v }))}
              options={(meta.units || []).map((u) => ({ value: u, label: u }))}
              data-testid="material-unit-select"
            />
          </Field>
          <Field label="Estoque mínimo">
            <Input type="number" min="0" step="0.01" value={form.min_stock} onChange={set("min_stock")} className="font-num" data-testid="material-min-stock-input" />
          </Field>
          <Field label="Fornecedor">
            <Input value={form.supplier} onChange={set("supplier")} data-testid="material-supplier-input" />
          </Field>
        </div>

        {!material && (
          <div className="grid gap-4 rounded-lg border border-white/10 bg-secondary/40 p-4 sm:grid-cols-2">
            <Field label="Quantidade comprada" hint="Entra no estoque agora">
              <Input type="number" min="0" step="0.01" value={form.initial_quantity} onChange={set("initial_quantity")} className="font-num" data-testid="material-initial-qty-input" />
            </Field>
            <Field label="Valor pago (R$)" hint="Sai do caixa no Financeiro">
              <Input type="number" min="0" step="0.01" value={form.paid_value} onChange={set("paid_value")} className="font-num" data-testid="material-paid-value-input" />
            </Field>
          </div>
        )}

        <Field label="Observações">
          <Textarea value={form.notes} onChange={set("notes")} rows={2} data-testid="material-notes-input" />
        </Field>

        <Button type="submit" disabled={busy} data-testid="save-material-btn" className="h-11 rounded-full font-semibold">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salvar
        </Button>
      </form>
    </ResponsiveModal>
  );
}

function MaterialMovementModal({ material, onClose, onSaved }) {
  const [type, setType] = useState("ENTRADA");
  const [quantity, setQuantity] = useState("");
  const [paid, setPaid] = useState("");
  const [supplier, setSupplier] = useState(material.supplier || "");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/material-movements", {
        material_id: material.id,
        type,
        quantity: Number(quantity || 0),
        paid_value: type === "ENTRADA" && paid !== "" ? Number(paid) : null,
        supplier,
        notes,
      });
      toast.success(
        type === "ENTRADA" ? "Compra registrada no estoque e no caixa." : "Movimentação registrada."
      );
      onClose();
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Movimentar ${material.name}`}
      description={`Disponível: ${Number(material.quantity || 0).toLocaleString("pt-BR")} ${material.unit}`}
      testid="material-movement-modal"
    >
      <form onSubmit={submit} className="mt-2 flex flex-col gap-4">
        <Field label="Tipo">
          <NativeSelect
            value={type}
            onChange={setType}
            options={[
              { value: "ENTRADA", label: "Compra / entrada" },
              { value: "CONSUMO", label: "Consumo / baixa" },
              { value: "AJUSTE", label: "Ajuste de inventário" },
            ]}
            data-testid="material-movement-type"
          />
        </Field>
        <Field label={`Quantidade (${material.unit})`}>
          <Input type="number" step="0.01" value={quantity} onChange={(e) => setQuantity(e.target.value)} required className="font-num" data-testid="material-movement-qty" />
        </Field>
        {type === "ENTRADA" && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Valor pago (R$)" hint="Vira saída no Financeiro">
              <Input type="number" min="0" step="0.01" value={paid} onChange={(e) => setPaid(e.target.value)} className="font-num" data-testid="material-movement-paid" />
            </Field>
            <Field label="Fornecedor">
              <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} data-testid="material-movement-supplier" />
            </Field>
          </div>
        )}
        <Field label="Observações">
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="material-movement-notes" />
        </Field>
        <Button type="submit" disabled={busy} data-testid="save-material-movement-btn" className="h-11 rounded-full font-semibold">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Registrar
        </Button>
      </form>
    </ResponsiveModal>
  );
}

function MaterialHistoryModal({ material, onClose }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api
      .get(`/materials/${material.id}/history`)
      .then(({ data }) => setRows(data))
      .catch((e) => toast.error(apiError(e)));
  }, [material.id]);

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Histórico de ${material.name}`}
      testid="material-history-modal"
    >
      <div className="mt-2 space-y-2">
        {!rows && <Loading label="Carregando..." />}
        {rows && rows.length === 0 && (
          <p className="py-6 text-sm text-muted-foreground">Nenhuma movimentação registrada.</p>
        )}
        {(rows || []).map((r) => (
          <div key={r.id} className="flex items-center justify-between gap-3 rounded-md border border-white/10 bg-secondary/40 px-3 py-2 text-sm" data-testid={`material-history-row-${r.id}`}>
            <div className="min-w-0">
              <p className="text-sm">{r.type === "ENTRADA" ? "Compra / entrada" : r.type === "CONSUMO" ? "Consumo" : "Ajuste"}</p>
              <p className="truncate text-xs text-muted-foreground">
                {dataBR(r.created_at)}
                {r.notes ? ` · ${r.notes}` : ""}
              </p>
            </div>
            <span className={`font-num text-sm ${r.type === "CONSUMO" ? "text-red-300" : "text-emerald-300"}`}>
              {r.type === "CONSUMO" ? "−" : "+"} {Number(r.quantity).toLocaleString("pt-BR")} {material.unit}
            </span>
          </div>
        ))}
      </div>
    </ResponsiveModal>
  );
}
